import {
  defaultValue,
  defined,
  OpenStreetMapImageryProvider,
  NaverStaticImageryProvider,
  NationwideOrthoImageryProvider,
  JulianDate,
  VWorldImageryProvider,
  CameraEventType,
  EllipsoidTerrainProvider,
  NgiiMapImageryProvider,
  ScreenSpaceEventType,
  Color,
  NationwideTerrainProvider,
  ModelGraphics,
  Model,
  Cesium3DTileset,
  // UrlTemplateImageryProvider,
  // Cesium3DTileset,
  // WebMercatorTilingScheme,
} from "@cesium/engine";
import {
  Viewer as CesiumViewer,
  ProviderViewModel,
  InteractionSplit,
  ConstantUrl,
  InteractionRemove,
  InteractionMove,
  InteractionScale,
  InteractionRotate,
  InteractionVisibility,
  InteractionClipping,
  PointEntity,
  LinestringEntity,
  PolygonEntity,
  MeasurementAltitude,
  MeasurementLengthStraight,
  MeasurementLengthGround,
  MeasurementLengthPlane,
  MeasurementLengthVertical,
  MeasurementLengthHorizontal,
  MeasurementArea,
  MeasurementVolume,
  AnalysisVisibility,
  Utils,
  DitapPaneManager,
} from "@cesium/widgets";
import { DateTime } from "luxon";

import InteractionAltitude from "../interaction/InteractionAltitude";
import InteractionLengthGround from "../interaction/InteractionLengthGround";
import InteractionLengthStraight from "../interaction/InteractionLengthStraight";
import InteractionLengthPlane from "../interaction/InteractionLengthPlane";
import InteractionLengthVertical from "../interaction/InteractionLengthVertical";
import InteractionLengthHorizontal from "../interaction/InteractionLengthHorizontal";
import InteractionSelect from "../interaction/InteractionSelect";
import AnalysisToolPicker from "./AnalysisToolPicker";
import DitapBaseLayerPicker from "./DitapBaseLayerPicker";
import DitapFullscreenButton from "./DitapFullscreenButton";
import DitapGeocoder from "./DitapGeocoder";
import DitapHomeButton from "./DitapHomeButton";
import DitapVworldGeocoderService from "./DitapVworldGeocoderService";
import DitapNavigationHelpButton from "./DitapNavigationHelpButton";
import DrawingTypePicker from "./DrawingTypePicker";
import EditToolPicker from "./EditToolPicker";
import MeasurementToolPicker from "./MeasurementToolPicker";
import InteractionArea from "../interaction/InteractionArea";
import InteractionVolume from "../interaction/InteractionVolume";
import InteractionLinestringEntity from "../interaction/InteractionLinestringEntity";
import InteractionPointEntity from "../interaction/InteractionPointEntity";
import InteractionPolygonEntity from "../interaction/InteractionPolygonEntity";
import PopupInput3D from "../popup/PopupInput3D";
import Interaction3DPosition from "../interaction/Interaction3DPosition";
import DitapPanelManager from "../panel/DitapPanelManager";

/*
 * Ditap 객체 저장소.<br>
 * Gets or sets Ditap object storage
 * @alias DitapStorage
 *
 * @constructor
 */
class DitapStorage {
  constructor() {
    this._measurementAltitude = {};
    this._measurementArea = {};
    this._measurementLengthGround = {};
    this._measurementLengthPlane = {};
    this._measurementLengthStraight = {};
    this._measurementLengthVertical = {};
    this._measurementLengthHorizontal = {};
    this._measurementVolume = {};
    this._analysisVisibility = {};
    this._analysisClipping = {};
    this._pointEntity = {};
    this._linestringEntity = {};
    this._polygonEntity = {};
    this._propertyPopups = {};
    this._stylePopups = {};
    this._volumePopups = {};
    this._visibilityPopups = {};
    this._input3DModel = {};
  }

  /*
   * 고도 계산 및 영역 표시 클래스.</br>
   * Altitude calculation and area display class.
   * @type {object}
   */
  set measurementAltitude(measurementAltitude) {
    this._measurementAltitude = measurementAltitude; // 원본 클래스
  }
  get measurementAltitude() {
    return this._measurementAltitude;
  }

  /*
   * 면적 계산 및 영역 표시 클래스.<br>
   * Area calculation and area display class.
   * @type {object}
   */
  set measurementArea(measurementArea) {
    this._measurementArea = measurementArea;
  }
  get measurementArea() {
    return this._measurementArea;
  }

  /*
   * 지면 거리 계산 및 영역 표시 클래스.<br>
   * Ground distance calculation and area display class.
   * @type {object}
   */
  set measurementLengthGround(measurementLengthGround) {
    this._measurementLengthGround = measurementLengthGround;
  }
  get measurementLengthGround() {
    return this._measurementLengthGround;
  }

  /*
   * 평면 거리 계산 및 영역 표시 클래스.<br>
   * Plane distance calculation and area display class.
   * @type {object}
   */
  set measurementLengthPlane(measurementLengthPlane) {
    this._measurementLengthPlane = measurementLengthPlane;
  }
  get measurementLengthPlane() {
    return this._measurementLengthPlane;
  }

  /*
   * 직선 거리 계산 및 영역 표시 클래스.<br>
   * Straight distance calculation and area display class.
   * @type {object}
   */
  get measurementLengthStraight() {
    return this._measurementLengthStraight;
  }
  set measurementLengthStraight(measurementLengthStraight) {
    this._measurementLengthStraight = measurementLengthStraight;
  }

  /*
   * 수직 거리 계산 및 영역 표시 클래스.<br>
   * Vertical distance calculation and area display class.
   * @type {object}
   */
  set measurementLengthVertical(measurementLengthVertical) {
    this._measurementLengthVertical = measurementLengthVertical;
  }
  get measurementLengthVertical() {
    return this._measurementLengthVertical;
  }

  /*
   * 수평 거리 계산 및 영역 표시 클래스.<br>
   * Horizontal distance calculation and area display class.
   * @type {object}
   */
  set measurementLengthHorizontal(measurementLengthHorizontal) {
    this._measurementLengthHorizontal = measurementLengthHorizontal;
  }
  get measurementLengthHorizontal() {
    return this._measurementLengthHorizontal;
  }

  /*
   * 체적 계산 및 영역 표시 클래스.<br>
   * Volume calculation and area display class.
   * @type {object}
   */
  get measurementVolume() {
    return this._measurementVolume;
  }
  set measurementVolume(measurementVolume) {
    this._measurementVolume = measurementVolume;
  }

  /*
   * 점 엔티티 생성 클래스.</br>
   * Class for creating point entities.
   * @type {object}
   */
  set pointEntity(pointEntity) {
    this._pointEntity = pointEntity;
  }
  get pointEntity() {
    return this._pointEntity;
  }

  /*
   * 선 엔티티 생성 클래스.</br>
   * Class for creating line entities.
   * @type {object}
   */
  set linestringEntity(linestringEntity) {
    this._linestringEntity = linestringEntity;
  }
  get linestringEntity() {
    return this._linestringEntity;
  }

  /*
   * 면 엔티티 생성 클래스.</br>
   * Class for creating polygon entities.
   * @type {object}
   */
  set polygonEntity(polygonEntity) {
    this._polygonEntity = polygonEntity;
  }
  get polygonEntity() {
    return this._polygonEntity;
  }

  /*
   * 가시성 분석 연산 및 영역 표시 클래스.<br>
   * Visibility analysis operation and area display class.
   * @type {object}
   */
  set analysisVisibility(analysisVisibility) {
    this._analysisVisibility = analysisVisibility;
  }
  get analysisVisibility() {
    return this._analysisVisibility;
  }

  /*
   * 절단면 분석 클래스.<br>
   * Visibility analysis operation and area display class.
   * @type {object}
   */
  set analysisClipping(analysisClipping) {
    this._analysisClipping = analysisClipping;
  }
  get analysisClipping() {
    return this._analysisClipping;
  }

  /*
   * 속성 팝업 객체.<br>
   * Object of PropertyPopups.
   * @type {object}
   */
  set propertyPopups(propertyPopups) {
    this._propertyPopups = propertyPopups;
  }
  get propertyPopups() {
    return this._propertyPopups;
  }

  /*
   * 스타일 팝업 객체.<br>
   * Object of StylePopups.
   * @type {object}
   */
  set stylePopups(stylePopups) {
    this._stylePopups = stylePopups;
  }
  get stylePopups() {
    return this._stylePopups;
  }

  /*
   * 체적 팝업 객체.<br>
   * Object of PopupMeasurementVolumes.
   * @type {object}
   */
  set volumePopups(volumePopups) {
    this._volumePopups = volumePopups;
  }
  get volumePopups() {
    return this._volumePopups;
  }

  /*
   * 가시성 분석 팝업 객체.<br>
   * Object of PopupAnalysisVisibility.
   * @type {object}
   */
  set visibilityPopups(visibilityPopups) {
    this._visibilityPopups = visibilityPopups;
  }
  get visibilityPopups() {
    return this._visibilityPopups;
  }

  /**
   * Object of added 3d model
   * @type {object}
   */
  set input3DModel(input3DModel) {
    this._input3DModel = input3DModel;
  }
  get input3DModel() {
    return this._input3DModel;
  }

  /*
   * 생성 도구를 통해 생성된 엔티티 중 해당 아이디 엔티티 조회.<br>
   * Get entities created by ID.
   * @param {string} id 엔티티 아이디.<br> Entity id.
   * @return {Entity} 찾은 엔티티.<br> Found entity.
   */
  getCreatedEntityById(id) {
    let entity;

    if (id.includes("PointEntity")) {
      // PointEntity
      entity = this.pointEntity[id];
    } else if (id.includes("LinestringEntity")) {
      // LinestringEntity
      const linestringIds = Object.keys(this.linestringEntity);

      linestringIds.forEach((lineStringId) => {
        if (id.includes(lineStringId)) {
          entity = this.linestringEntity[lineStringId];
        }
      });
    } else if (id.includes("PolygonEntity")) {
      //PolygonEntity
      const polygonIds = Object.keys(this.polygonEntity);

      polygonIds.forEach((polygonId) => {
        if (id.includes(polygonId)) {
          entity = this.polygonEntity[polygonId];
        }
      });
    }

    return entity;
  }

  /*
   * 측정 도구를 통해 생성된 엔티티 중 해당 아이디 엔티티 조회.
   * Get entities created by the measurement tool by ID
   * @param {string} id 엔티티 아이디.<br> Entity id.
   * @return {Entity} 찾은 엔티티.<br> Found entity.
   */
  getMeasurementEntityById(id) {
    let entity;

    if (id.includes("MeasurementAltitude")) {
      // Altitude
      entity = this.measurementAltitude[id];
    } else if (id.includes("MeasurementLengthStraight")) {
      // LinestringEntity
      const straightIds = Object.keys(this.measurementLengthStraight);

      straightIds.forEach((straightId) => {
        if (id.includes(straightId)) {
          entity = this.measurementLengthStraight[straightId];
        }
      });
    } else if (id.includes("MeasurementLengthGround")) {
      //PolygonEntity
      const groundIds = Object.keys(this.measurementLengthGround);

      groundIds.forEach((groundId) => {
        if (id.includes(groundId)) {
          entity = this.measurementLengthGround[groundId];
        }
      });
    } else if (id.includes("MeasurementLengthPlane")) {
      //PolygonEntity
      const planeIds = Object.keys(this.measurementLengthPlane);

      planeIds.forEach((planeId) => {
        if (id.includes(planeId)) {
          entity = this.measurementLengthPlane[planeId];
        }
      });
    } else if (id.includes("MeasurementLengthVertical")) {
      //PolygonEntity
      const verticalIds = Object.keys(this.measurementLengthVertical);

      verticalIds.forEach((verticalId) => {
        if (id.includes(verticalId)) {
          entity = this.measurementLengthVertical[verticalId];
        }
      });
    } else if (id.includes("MeasurementLengthHorizontal")) {
      //PolygonEntity
      const horizontalIds = Object.keys(this.measurementLengthHorizontal);

      horizontalIds.forEach((horizontalId) => {
        if (id.includes(horizontalId)) {
          entity = this.measurementLengthHorizontal[horizontalId];
        }
      });
    } else if (id.includes("MeasurementArea")) {
      //PolygonEntity
      const areaIds = Object.keys(this.measurementArea);

      areaIds.forEach((areaId) => {
        if (id.includes(areaId)) {
          entity = this.measurementArea[areaId];
        }
      });
    } else if (id.includes("MeasurementVolume")) {
      //PolygonEntity
      const volumeIds = Object.keys(this.measurementVolume);

      volumeIds.forEach((volumeId) => {
        if (id.includes(volumeId)) {
          entity = this.measurementVolume[volumeId];
        }
      });
    }

    return entity;
  }

  /*
   * 분석 도구를 통해 생성된 엔티티 중 해당 아이디 엔티티 조회.
   * Get entities created by the analysis tool by ID
   * @param {string} id 엔티티 아이디.<br> Entity id.
   * @return {Entity} 찾은 엔티티.<br> Found entity.
   */
  getAnalysisEntityById(id) {
    let entity;

    if (id.includes("AnalysisVisibility")) {
      // Visibility
      const visibilityIds = Object.keys(this.analysisVisibility);

      visibilityIds.forEach((visibilityId) => {
        if (id.includes(visibilityId)) {
          entity = this.analysisVisibility[visibilityId];
        }
      });
    } else if (id.includes("AnalysisClipping")) {
      // Clipping
      const clippingIds = Object.keys(this.analysisClipping);

      clippingIds.forEach((clippingId) => {
        if (id.includes(clippingId)) {
          entity = this.analysisClipping[clippingId];
        }
      });
    }
    return entity;
  }

  /**
   * Object를 통해 AnalysisClippint 인스턴스 조회
   * @param {Model | Cesium3DTileset | ModelGraphics} object
   * @returns {AnalysisClipping | undefined}
   */
  getAnalysisClippingByObject(object) {
    if (
      object instanceof Model ||
      object instanceof Cesium3DTileset ||
      object instanceof ModelGraphics
    ) {
      const analysisClippings = Object.values(this.analysisClipping);
      for (const analysisClipping of analysisClippings) {
        if (analysisClipping.selectObject === object) {
          return analysisClipping;
        }
      }
    }

    return;
  }

  /*
   * 아이디로 엔티티 조회.<br>
   * Find entity instance by id.
   * @param {string} id 엔티티 아이디.<br> Entity id.
   * @return {Entity} 찾은 엔티티.<br> Found entity.
   */
  getInstanceById(id) {
    let entity;

    if (id) {
      if (id.includes("Measurement")) {
        entity = this.getMeasurementEntityById(id);
      } else if (id.includes("Analysis")) {
        entity = this.getAnalysisEntityById(id);
      } else {
        entity = this.getCreatedEntityById(id);
      }
    }
    return entity;
  }

  /*
   * 다이탭뷰어 스토리지에 저장된 다이탭 엔티티조회.</br>
   * Get DitapEntity Saved in DitapViewer Storage.
   * @param {object} pickedObject 선택된 객체.</br> The selected object.
   * @returns {Entity} 다이탭 엔티티.</br> The DitapEntity.
   */
  getDitapEntity(pickedObject) {
    let ditapEntity;

    // 뷰어에 저장된 엔티티인지 확인
    const entityId = pickedObject.id?.id || pickedObject.id;
    const storageEntity =
      typeof entityId === "string" ? this.getInstanceById(entityId) : null;

    if (this.isDitapEntity(pickedObject)) {
      ditapEntity = pickedObject;
    } else if (storageEntity && this.isDitapEntity(storageEntity)) {
      ditapEntity = storageEntity;
    }

    return ditapEntity;
  }

  isDitapEntity(entity) {
    const entityClasses = [
      PointEntity,
      LinestringEntity,
      PolygonEntity,
      MeasurementAltitude,
      MeasurementLengthStraight,
      MeasurementLengthGround,
      MeasurementLengthPlane,
      MeasurementLengthVertical,
      MeasurementLengthHorizontal,
      MeasurementArea,
      MeasurementVolume,
      AnalysisVisibility,
    ];
    for (const entityClass of entityClasses) {
      if (entity instanceof entityClass) {
        return true;
      }
    }
    return false;
  }

  /*
   * 아이디로 엔티티 제거.<br>
   * Remove entity instance by id
   * @param {string} id Entity id.
   */
  removeInstanceById(id) {
    const allInstances = [
      this.measurementAltitude,
      this.measurementArea,
      this.measurementLengthGround,
      this.measurementLengthPlane,
      this.measurementLengthStraight,
      this.measurementLengthVertical,
      this.measurementLengthHorizontal,
      this.measurementVolume,
      this.pointEntity,
      this.linestringEntity,
      this.polygonEntity,
      this.analysisVisibility,
    ];

    allInstances.forEach((instance) => {
      if (instance[id]) {
        delete instance[id];
      }
    });
  }

  /*
   * 아이디로 팝업 인스턴스 제거.<br>
   * Remove popup instance by id.
   * @param {string} id 엔티티 아이디.<br> Entity id.
   */
  removePopupInstanceById(id) {
    const allInstances = [
      this.stylePopups,
      this.propertyPopups,
      this.volumePopups,
      this.visibilityPopups,
    ];

    allInstances.forEach((instance) => {
      if (instance[id]) {
        delete instance[id];
      }
    });
  }

  /*
   * 아이디로 팝업 인스턴스 조회.<br>
   * Return popup instance by id
   * @param {string} id 엔티티 아이디.<br> Entity id.
   */
  getPopupInstanceById(id) {
    let popup;
    const allInstances = [
      this.stylePopups,
      this.propertyPopups,
      this.volumePopups,
      this.visibilityPopups,
    ];

    for (const instanceObject of allInstances) {
      if (instanceObject[id]) {
        popup = instanceObject[id];
        break;
      }
    }

    return popup;
  }
}

/*
 * ViewModel의 시간 형식을 한국어 문자열로 변환.<br>
 * Converts the time format in viewModel to a Korean string.
 * @param {AnimationViewModel.DateFormatter} 변환할 시간.<br> datetime
 * @param {string} type 문자열 형식.<br> Date Type.
 * @returns {object} 변환된 시간.<br> object of date time.
 *
 * @see {@link https://moment.github.io/luxon/api-docs/index.html}
 */
function localeDateTimeFormatter(datetime, type) {
  const isoString = JulianDate.toIso8601(datetime);
  const dateTime = DateTime.fromISO(isoString);

  let objDT;
  if (type === "time") {
    objDT = dateTime.toLocaleString(DateTime.TIME_SIMPLE);
  } else if (type === "datetime") {
    objDT = dateTime.toLocaleString({
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  } else {
    objDT = dateTime.toLocaleString({
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return objDT;
}

/**
 * @typedef {object} DitapViewer.ConstructorOptions
 * Viewer 생성자의 초기화 옵션.<br>
 * Initialization options for the Viewer constructor
 *
 *
 * @property {object} editingTools 다음과 같은 속성을 가진 편집 객체.<br> Object of editingTools with the following properties:
 * @property {object} editingTools.createTools 다음과 같은 속성을 가진 생성 객체.<br> Object of createTools with the following properties:
 * @property {boolean} editingTools.createTools.point false로 설정 시 점 생성 위젯은 생성되지 않음.<br> If set to false, the point widget will not be created.
 * @property {boolean} editingTools.createTools.linestring false로 설정 시 선 생성 위젯은 생성되지 않음.<br> If set to false, the linestring widget will not be created.
 * @property {boolean} editingTools.createTools.polygon false로 설정 시 면 생성 위젯은 생성되지 않음.<br> If set to false, the polygon widget will not be created.
 * @property {boolean} editingTools.select false로 설정 시 선택 위젯은 생성되지 않음.<br> If set to false, the select widget will not be created.
 * @property {boolean} editingTools.move false로 설정 시 이동 위젯은 생성되지 않음.<br> If set to false, the move widget will not be created.
 * @property {boolean} editingTools.rotate false로 설정 시 회전 위젯은 생성되지 않음.<br> If set to false, the rotate widget will not be created.
 * @property {boolean} editingTools.scale false로 설정 시 크기조절 위젯은 생성되지 않음.<br> If set to false, the scale widget will not be created.
 * @property {boolean} editingTools.remove false로 설정 시 삭제 위젯은 생성되지 않음.<br>If set to false, the remove widget will not be created.
 * @property {object} analysisTools 다음과 같은 속성을 가진 분석 객체.<br> Object of analysisTools with the following properties:
 * @property {boolean} analysisTools.visibility false로 설정 시 가시성 위젯은 생성되지 않음.<br> If set to false, the visibility widget will not be created.
 * @property {boolean} analysisTools.clipping false로 설정 시 절단면 위젯은 생성되지 않음.<br> If set to false, the clipping widget will not be created.
 * @property {boolean} analysisTools.split false로 설정 시 분할 위젯은 생성되지 않음.<br> If set to false, the split widget will not be created.
 * @property {object} measurementTools 다음과 같은 속성을 가진 측정 객체.<br> Object of measurementTools with the following properties:
 * @property {boolean} measurementTools.altitude false로 설정 시 고도 측정 위젯은 생성되지 않음.<br> If set to false, the altitude widget will not be created.
 * @property {boolean} measurementTools.straight false로 설정 시 직선거리 측정 위젯은 생성되지 않음.<br> If set to false, the straight widget will not be created.
 * @property {boolean} measurementTools.ground false로 설정 시 지면거리 측정 위젯은 생성되지 않음.<br> If set to false, the ground widget will not be created.
 * @property {boolean} measurementTools.plane false로 설정 시 평면거리 측정 위젯은 생성되지 않음.<br> If set to false, the plane widget will not be created.
 * @property {boolean} measurementTools.vertical false로 설정 시 수직거리 측정 위젯은 생성되지 않음.<br> If set to false, the vertical widget will not be created.
 * @property {boolean} measurementTools.horizontal false로 설정 시 수평거리 측정 위젯은 생성되지 않음.<br> If set to false, the horizontal widget will not be created.
 * @property {boolean} measurementTools.area false로 설정 시 면적 측정 위젯은 생성되지 않음.<br> If set to false, the area widget will not be created.
 * @property {boolean} measurementTools.volume false로 설정 시  체적 측정 위젯은 생성되지 않음.<br> If set to false, the volume widget will not be created.
 * @property {boolean} [animation=true] false로 설정 시 애니메이션 위젯은 생성되지 않음.<br> If set to false, the Animation widget will not be created.
 * @property {boolean} [baseLayerPicker=true] false로 설정 시 지도 선택 위젯은 생성되지 않음.<br> If set to false, the BaseLayerPicker widget will not be created.
 * @property {boolean} [fullscreenButton=true] false로 설정 시 전체화면 위젯은 생성되지 않음.<br>If set to false, the FullscreenButton widget will not be created.
 * @property {boolean} [vrButton=false] false로 설정 시 VR 버튼 위젯은 생성되지 않음.<br>If set to true, the VRButton widget will be created.
 * @property {boolean|GeocoderService[]} [geocoder=true] false로 설정 시 주소검색 위젯은 생성되지 않음.<br>If set to false, the Geocoder widget will not be created.
 * @property {boolean} [homeButton=true] false로 설정 시 처음화면 위젯은 생성되지 않음.<br>If set to false, the HomeButton widget will not be created.
 * @property {boolean} [timeline=true] false로 설정 시 타임라인 위젯은 생성되지 않음.<br>If set to false, the Timeline widget will not be created.
 * @property {boolean} [navigationHelpButton=true] false로 설정 시 navigationHelpButton 위젯은 생성되지 않음.<br>If set to false, the navigation help button will not be created.
 * @property {boolean} [navigationInstructionsInitiallyVisible=true] 초기에 네비게이션 지침이 표시되어야 하는 경우 true, 사용자가 명시적으로 버튼을 클릭할 때까지 표시되지 않아야 하는 경우 false.<br> True if the navigation instructions should initially be visible, or false if the should not be shown until the user explicitly clicks the button.
 * @property {boolean} [scene3DOnly=false] true인 경우 3D에서만 렌더링 됨.<br> When <code>true</code>, each geometry instance will only be rendered in 3D to save GPU memory.
 * @property {boolean} [shouldAnimate=false] 시뮬레이션 시간을 진행시키려면 true, 그렇지 않은 경우 false<br> <code>true</code> if the clock should attempt to advance simulation time by default, <code>false</code> otherwise.  This option takes precedence over setting
 * {@link Viewer#clockViewModel}.
 * @property {ClockViewModel} [clockViewModel=new ClockViewModel(clock)] 현재 시간을 제어하기 위해 사용할 시계 뷰 모델.<br> The clock view model to use to control current time.
 * @property {ProviderViewModel} [selectedImageryProviderViewModel] 현재의 기본 이미지 레이어에 대한 뷰 모델. baseLayerPicker가 true로 설정된 경우에만 유효.<br> The view model for the current base imagery layer, if not supplied the first available base layer is used.  This value is only valid if `baseLayerPicker` is set to true.
 * @property {ProviderViewModel[]} [imageryProviderViewModels=createDefaultImageryProviderViewModels()] BaseLayerPicker에서 선택 가능한 ProviderViewModels의 배열. baseLayerPicker가 true로 설정된 경우에만 유효.<br> The array of ProviderViewModels to be selectable from the BaseLayerPicker.  This value is only valid if `baseLayerPicker` is set to true.
 * @property {ProviderViewModel} [selectedTerrainProviderViewModel] 현재 베이스 지형 레이어의 뷰 모델. 제공되지 않은 경우 첫 번째 사용 가능한 베이스 레이어가 사용됨. baseLayerPicker가 true로 설정된 경우에만 유효.<br> The view model for the current base terrain layer, if not supplied the first available base layer is used.  This value is only valid if `baseLayerPicker` is set to true.
 * @property {ImageryProvider|false} [imageryProvider=createWorldImagery()] 사용할 imageryProvider. baseLayerPicker가 false로 설정된 경우에만 유효.<br> The imagery provider to use.  This value is only valid if `baseLayerPicker` is set to false. Deprecated.
 * @property {ImageryLayer|false} [baseLayer=ImageryLayer.fromWorldImagery()] 지구에 적용되는 가장 하단의 이미지 레이어. baseLayerPicker가 false로 설정된 경우에만 유효.<br> The bottommost imagery layer applied to the globe. If set to <code>false</code>, no imagery provider will be added. This value is only valid if `baseLayerPicker` is set to false.
 * @property {TerrainProvider} [terrainProvider=new EllipsoidTerrainProvider()] 사용할 terrain provider.<br> The terrain provider to use
 * @property {Terrain} [options.terrain] 지형 공급자로 사용할 지형 객체. terrainProvider가 되지 않은 경우에만 지정가능.<br> A terrain object which handles asynchronous terrain provider. Can only specify if options.terrainProvider is undefined.
 * @property {SkyBox|false} [skyBox] 별을 렌더링하기 위해 사용. false로 설정 시 태양, 달이 추가되지 않음.<br> <br> The skybox used to render the stars.  When <code>undefined</code>, the default stars are used. If set to <code>false</code>, no skyBox, Sun, or Moon will be added.
 * @property {SkyAtmosphere|false} [skyAtmosphere] 대기(SkyAtmosphere). false로 설정 시 대기 효과 제거.<br> Blue sky, and the glow around the Earth's limb.  Set to <code>false</code> to turn it off.
 * @property {Element|string} [fullscreenElement=document.body] 전체화면 모드 요소. 기본적으로 <body> 사용.<br>The element or id to be placed into fullscreen mode when the full screen button is pressed.
 * @property {boolean} [useDefaultRenderLoop=true] 렌더링 루프 제어 여부.<br> True if this widget should control the render loop, false otherwise.
 * @property {number} [targetFrameRate] 기본 렌더링 루프 사용할 경우 대상 프레임 속도 지정.<br> The target frame rate when using the default render loop.
 * @property {boolean} [showRenderLoopErrors=true] 렌더링 루프 오류 발생 시 오류 표시 여부.<br> If true, this widget will automatically display an HTML panel to the user containing the error, if a render loop error occurs.
 * @property {boolean} [useBrowserRecommendedResolution=true] true인 경우 권장 해상도로 렌더링.<br> If true, render at the browser's recommended resolution and ignore <code>window.devicePixelRatio</code>.
 * @property {boolean} [automaticallyTrackDataSourceClocks=true] true인 경우 새로 추가된 데이터소스의 시계 설정 자동 추적, false 인 경우 시계를 독립적으로 구성 가능.<br> If true, this widget will automatically track the clock settings of newly added DataSources, updating if the DataSource's clock changes.  Set this to false if you want to configure the clock independently.
 * @property {ContextOptions} [contextOptions]  Scene에 전달되는 컨텍스트 및 WebGL 생성 속성을 지정.<br> Context and WebGL creation properties passed to
 * {@link Scene}.
 * @property {SceneMode} [sceneMode=SceneMode.SCENE3D] 초기 장면 모드 설정. 기본값은 3D 장면 모드.<br> The initial scene mode.
 * @property {MapProjection} [mapProjection=new GeographicProjection()] 2D 및 Columbus View 모드에서 사용할 지도 투영(Map Projection)을 설정.<br> The map projection to use in 2D and Columbus View modes.
 * @property {Globe|false} [globe=new Globe(mapProjection.ellipsoid)] 장면에서 사용할 지구(Globe)를 설정.<br> The globe to use in the scene.  If set to <code>false</code>, no globe will be added.
 * @property {boolean} [orderIndependentTranslucency=true] true인 경우 독립적인 투명도 사용.<br> If true and the configuration supports it, use order independent translucency.
 * @property {Element|string} [creditContainer] CreditDisplay를 포함할 DOM 요소나 ID.<br> The DOM element or ID that will contain the
 * {@link CreditDisplay}. 지정되지 않은 경우, 크레딧은 위젯 자체의 아래에 추가.<br> If not specified, the credits are added to the bottom of the widget itself.
 * @property {Element|string} [creditViewport] 크레딧 팝업(credit pop-up)을 포함할 DOM 엘리먼트 또는 ID. <br> The DOM element or ID that will contain the credit pop up created by the
 * {@link CreditDisplay}. 지정되지 않은 경우, 크레딧은 위젯 자체의 위에 추가. If not specified, it will appear over the widget itself.
 * @property {DataSourceCollection} [dataSources=new DataSourceCollection()] 위젯에서 시각화하는 데이터소스의 컬렉션 설정.<br> The collection of data sources visualized by the widget.  If this parameter is provided,<br> the instance is assumed to be owned by the caller and will not be destroyed when the viewer is destroyed.
 * @property {boolean} [shadows=false] 그림자 투사 여부.<br> Determines if shadows are cast by light sources.
 * @property {ShadowMode} [terrainShadows=ShadowMode.RECEIVE_ONLY] 그림자 투사 방법.<br> Determines if the terrain casts or receives shadows from light sources.
 * @property {MapMode2D} [mapMode2D=MapMode2D.INFINITE_SCROLL] 2D 지도 회전여부 또는 수평 무한 스크롤 가능 여부.<br> Determines if the 2D map is rotatable or can be scrolled infinitely in the horizontal direction.
 * @property {boolean} [blurActiveElementOnCanvasFocus=true] true인 경우 뷰어의 캔버스가 클릭될 때 활성 요소의 포커스 잃음, false인 경우 캔버스가 활성 요소로 설정되지 않고 위치나 엔티티 데이터를 가져오기 위해 클릭될 때 유용.<br> If true, the active element will blur when the viewer's canvas is clicked. Setting this to false is useful for cases when the canvas is clicked only for retrieving position or an entity data without actually meaning to set the canvas to be the active element.
 * @property {boolean} [requestRenderMode=false] true인 경우 scene 내에서 변경 사항에 따라 필요에 경우 프레임 렌더링 발생.<br> If true, rendering a frame will only occur when needed as determined by changes within the scene. Enabling reduces the CPU/GPU usage of your application and uses less battery on mobile, but requires using
 * {@link Scene#requestRender} 새 프레임을 명시적으로 렌더링하려면 render 함수 사용해야 함.<br> to render a new frame explicitly in this mode. This will be necessary in many cases after making changes to the scene in other parts of the API. See {@link https://cesium.com/blog/2018/01/24/cesium-scene-rendering-performance/|Improving Performance with Explicit Rendering}.
 * @property {number} [maximumRenderTimeChange=0.0] requestRenderMode가 true로 설정된 경우 렌더 요청 전에 허용되는 최대 시뮬레이션 시간 변경을 정의.<br>  If requestRenderMode is true, this value defines the maximum change in simulation time allowed before a render is requested. See {@link https://cesium.com/blog/2018/01/24/cesium-scene-rendering-performance/|Improving Performance with Explicit Rendering}.
 * @property {number} [depthPlaneEllipsoidOffset=0.0] 깊이 평면을 조정하여 타원체의 제로 고도 아래 렌더링 artefacts 해결.<br> Adjust the DepthPlane to address rendering artefacts below ellipsoid zero elevation.
 * @property {number} [msaaSamples=1] 다중 샘플 안티앨리어싱(MSAA)의 속도 제어. 일반적으로 다중 샘플링 비율은 픽셀당 2,4 및 8샘플이다.<br> If provided, this value controls the rate of multisample antialiasing. Typical multisampling rates are 2, 4, and sometimes 8 samples per pixel. Higher sampling rates of MSAA may impact performance in exchange for improved visual quality. This value only applies to WebGL2 contexts that support multisample render targets.
 */

/**
 * 응용 프로그램을 개발하기 위한 기본 위젯. 이 위젯은 모든 표준 Ditap 위젯을 하나의 재사용 가능한 패키지로 구성.<br>
 * 언제든지 다양한 응용 프로그램에 유용한 기능을 추가하는 Mixin을 사용하여 확장 가능.<br>
 * A base widget for building applications.  It composites all of the standard Ditap widgets into one reusable package.<br>
 * The widget can always be extended by using mixins, which add functionality useful for a variety of applications.
 * @extends Viewer
 *
 * @alias DitapViewer
 * @constructor
 *
 *
 * @param {DitapViewer.ConstructorOptions} [options] 초기화 옵션을 설명하는 객체.<br> Object describing initialization options.
 *
 * @example
 * // 위젯 비/활성화
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *   // 편집, 분석, 측정 도구 활성화
 *   // 1. 활성화 - 전체 활성화(true) 또는 부분 활성화(활성화 시킬 기능만 true)
 *   // 2. 비활성화 - false 또는 전달 안함
 *   editingTools: {
 *     select: true,
 *     createTools: {
 *       point: true,
 *       linestring: true,
 *       polygon: true,
 *     },
 *     remove: true,
 *     move: true,
 *     rotate: true,
 *     scale: true,
 *     upload: true,
 *   },
 *   analysisTools: {
 *     visibility: true,
 *     clipping: true,
 *     split: true,
 *   },
 *   measurementTools: {
 *     altitude: true,
 *     straight: true,
 *     ground: true,
 *     plane: true,
 *     vertical: true,
 *     horizontal: true,
 *     area: true,
 *     volume: true,
 *   },
 *   // 처음화면
 *   // 1. 활성화 - 기본 위치로 이동(true) 또는 초기 위치 지정(Cartesian3)
 *   // 2. 비활성화 - false 또는 전달 안함
 *   homeButton: true,
 *   navigationHelpButton: true, // 도움말 버튼(true/false)
 *   fullscreenButton: true, // 전체 화면(true/false)
 *   baseLayerPicker: true, // 배경 지도(true/false)
 * });
 */
class DitapViewer extends CesiumViewer {
  constructor(container, props) {
    if (!props) {
      props = {};
    }

    // 전체화면
    let ditapFullscreenButton = false;
    if (props.fullscreenButton === true) {
      ditapFullscreenButton = true;
    }

    // 배경지도
    let ditapBaseLayerPicker = false;
    if (props.baseLayerPicker === true) {
      ditapBaseLayerPicker = true;
    }

    // 도움말
    let ditapNavigationHelpButton = false;
    if (props.navigationHelpButton === true) {
      ditapNavigationHelpButton = true;
    }

    // 홈
    let ditapHomeButton = false;
    let destination;
    if (props.homeButton !== undefined) {
      const type = typeof props.homeButton;

      if (type === "boolean") {
        if (props.homeButton) {
          // true
          ditapHomeButton = true;
        }
      } else if (type === "object") {
        if (defined(props.homeButton)) {
          ditapHomeButton = true;
          destination = props.homeButton;
        }
      }
    }

    // 주소/좌표 검색
    let ditapGeocoder = false;
    let geocoderKey;
    if (props.geocoder) {
      if (
        typeof props.geocoder?.key === "string" &&
        props.geocoder?.key.trim() !== ""
      ) {
        ditapGeocoder = true;
        geocoderKey = props.geocoder.key;
      }
    }

    // 편집 도구
    let editToolPicker = false;
    if (props.editingTools !== undefined) {
      const type = typeof props.editingTools; // 타입 체크
      if (type === "boolean") {
        // 타입이 boolean
        if (props.editingTools) {
          // true
          props.editingTools = "all";
          editToolPicker = true;
        }
      } else if (type === "object") {
        // 타입이 object
        if (Object.keys(props.editingTools).length > 0) {
          // 객체가 비어있지 않은 경우
          editToolPicker = true;
        }
      }
    }

    // 분석 도구
    let analysisToolPicker = false;
    if (props.analysisTools !== undefined) {
      const type = typeof props.analysisTools;
      if (type === "boolean") {
        // 타입이 boolean
        if (props.analysisTools) {
          // true
          props.analysisTools = "all";
          analysisToolPicker = true;
        }
      } else if (type === "object") {
        // 타입이 object
        if (Object.keys(props.analysisTools).length > 0) {
          // 객체가 비어있지 않은 경우
          analysisToolPicker = true;
        }
      }
    }

    //측정도구
    let measurementToolPicker = false;
    if (props.measurementTools !== undefined) {
      const type = typeof props.measurementTools;
      if (type === "boolean") {
        // 타입이 boolean
        if (props.measurementTools) {
          // true
          props.measurementTools = "all";
          measurementToolPicker = true;
        }
      } else if (type === "object") {
        // 타입이 object
        if (Object.keys(props.measurementTools).length > 0) {
          // 빈 객체가 아닐 때
          measurementToolPicker = true;
        }
      }
    }

    // 기본 선택 막기
    props.baseLayerPicker = false;
    props.infoBox = false;
    props.sceneModePicker = false;
    props.selectionIndicator = false;
    props.fullscreenButton = false;
    props.navigationHelpButton = false;
    props.homeButton = false;
    props.geocoder = false;

    props.ditapSelectedImageryProviderViewModel =
      props.selectedImageryProviderViewModel;
    props.selectedImageryProviderViewModel = undefined;
    props.ditapSelectedTerrainProviderViewModel =
      props.selectedTerrainProviderViewModel;
    props.selectedTerrainProviderViewModel = undefined;

    super(container, props);

    this.ditapBaseLayerPicker = ditapBaseLayerPicker;
    this.ditapNavigationHelpButton = ditapNavigationHelpButton;

    if (editToolPicker || analysisToolPicker || measurementToolPicker) {
      this.isToolPicker = true;
    }

    this._ditapPaneManager = new DitapPaneManager(this);

    // 속성타입 지정(팝업/판넬)
    // if (props.editingTools?.property) {
    //   const property = this.checkPropertyOptions(props.editingTools.property);
    //   if (
    //     property?.type === "panel" &&
    //     !this.container.classList.contains("ditap-viewer")
    //   ) {
    //     this.container.classList.add("ditap-viewer");
    //   }

    //   // 패널 매니저 생성
    //   this.ditapPanelManager = new DitapPanelManager();
    // }
    // 패널 매니저 생성
    if (!this.container.classList.contains("ditap-viewer")) {
      this.container.classList.add("ditap-viewer");
    }
    this.ditapPanelManager = new DitapPanelManager();

    if (
      editToolPicker ||
      analysisToolPicker ||
      measurementToolPicker ||
      ditapHomeButton ||
      ditapNavigationHelpButton ||
      ditapFullscreenButton ||
      ditapGeocoder ||
      ditapBaseLayerPicker
    ) {
      // 모바일 전용 헤더 생성
      this.createMobileToolbar(this);
    }
    // 툴바 컨테이너 생성
    this.createToolBar(this);

    this.toolbarTopDiv = this.container.getElementsByClassName(
      "js-ditap-toolbal-top-wrap"
    )[0];
    this.toolbarTopBtnDiv = this.container.getElementsByClassName(
      "js-ditap-toolbal-top-btn-wrap"
    )[0];
    this.toolbarTopBtn1 = this.container.getElementsByClassName(
      "js-ditap-toolbal-top-btn1"
    )[0];
    this.toolbarTopBtn2 = this.container.getElementsByClassName(
      "js-ditap-toolbal-top-btn2"
    )[0];

    this.toolbarRightBtn = this.container.getElementsByClassName(
      "js-ditap-toolbal-right-btn-wrap"
    )[0];

    this.toolbarRightUl = this.container.getElementsByClassName(
      "js-ditap-toolbar-right-list"
    )[0];

    this.mobileBtnContainer = this.container.getElementsByClassName(
      "ditap-func-btn-list"
    )[0];

    // 홈
    if (ditapHomeButton) {
      this.ditapHomeButton = new DitapHomeButton(this, destination);
    }

    //-----------------------------보경 start---------------------------------
    // 배경지도
    const createBaseLayerPicker = ditapBaseLayerPicker;

    if (createBaseLayerPicker) {
      // 배경지도 이미지 객체 배열
      const imageryProviderViewModels = defaultValue(
        props.imageryProviderViewModels,
        this.createDefaultImageryProviderViewModels()
      );

      // 배경지도 터레인 객체 배열 -> 사용시 주석해제하세요
      const terrainProviderViewModels = defaultValue(
        props.terrainProviderViewModels,
        this.createDefaultTerrainProviderViewModels()
      );

      const toolbar = this.toolbarTopBtn1;

      this.ditapBaseLayerPicker = new DitapBaseLayerPicker(
        toolbar,
        {
          globe: this.cesiumWidget.scene.globe,
          imageryProviderViewModels: imageryProviderViewModels,
          selectedImageryProviderViewModel:
            props.ditapSelectedImageryProviderViewModel,
          terrainProviderViewModels: terrainProviderViewModels,
          selectedTerrainProviderViewModel:
            props.selectedTerrainProviderViewModel,
        },
        this
      );

      if (!ditapHomeButton) {
        if (!ditapNavigationHelpButton) {
          this.ditapBaseLayerPicker.mapPopup.classList.add(
            "right-m-11",
            "right-p-20"
          );
        } else {
          this.ditapBaseLayerPicker.mapPopup.classList.add(
            "right-p-51",
            "right-p-94"
          );
        }
      } else if (!ditapNavigationHelpButton) {
        this.ditapBaseLayerPicker.mapPopup.classList.add(
          "right-p-134",
          "right-p-61"
        );
      }
    }

    if (defined(props.imageryProvider) && props.imageryProvider !== false) {
      if (createBaseLayerPicker) {
        ditapBaseLayerPicker.viewModel.selectedImagery = undefined;
      }
      this.cesiumWidget.scene.imageryLayers.removeAll();
      this.cesiumWidget.scene.imageryLayers.addImageryProvider(
        props.imageryProvider
      );
    }

    if (defined(props.terrainProvider)) {
      if (createBaseLayerPicker) {
        this.ditapBaseLayerPicker.viewModel.selectedTerrain = undefined;
      }

      this.cesiumWidget.scene.terrainProvider = props.terrainProvider;
    }
    //-----------------------------보경 end---------------------------------

    // 검색
    if (ditapGeocoder) {
      const geocoderContainer = this.toolbarTopDiv;

      const geocoderService = new DitapVworldGeocoderService(geocoderKey);

      const geocoderOptions = {
        scene: this.scene,
        container: geocoderContainer,
        geocoderService: geocoderService,
      };

      this.ditapGeocoder = new DitapGeocoder(geocoderOptions, this);
    }

    //-----------------하위 interaction생성------------------------
    // 편집, 분석, 측정 인스턴스 생성
    this.toolPickerContainer = this.toolbarRightUl;

    if (
      !ditapGeocoder &&
      !ditapHomeButton &&
      !ditapBaseLayerPicker &&
      !ditapNavigationHelpButton
    ) {
      const container = this.container.getElementsByClassName(
        "ditap-toolbar-right-btn-wrap"
      )[0];

      container.classList.add("top");
    }

    // 1.편집
    if (editToolPicker) {
      const editingTools = props.editingTools;

      // 편집 하위 객체 배열
      if (editingTools === "all") {
        // 값이 undefined인 경우 default 전체 활성화
        this.editCreateObjArray = [
          new InteractionPointEntity(),
          new InteractionLinestringEntity(),
          new InteractionPolygonEntity(),
        ];

        this.drawingTypePicker = new DrawingTypePicker(
          this,
          this.editCreateObjArray
        );

        this.editUploadObj = new Interaction3DPosition();

        this.popupInput3D = new PopupInput3D(this, this.editUploadObj);

        this.editObjArray = [
          this.drawingTypePicker,
          new InteractionSelect(
            this,
            this.checkSelectOptions(props.editingTools.select),
            this.checkPropertyOptions(props.editingTools.property)
          ),
          new InteractionRemove(
            this.checkSelectOptions(props.editingTools.remove)
          ),
          new InteractionMove(this),
          new InteractionScale(this),
          new InteractionRotate(this),
          this.popupInput3D,
        ];
      } else {
        this.editCreateObjArray = [];
        this.editObjArray = [];
        for (const editTool in editingTools) {
          if (editTool === "createTools") {
            if (editingTools[editTool] === true) {
              this.editCreateObjArray = [
                new InteractionPointEntity(),
                new InteractionLinestringEntity(),
                new InteractionPolygonEntity(),
              ];
              this.drawingTypePicker = new DrawingTypePicker(
                this,
                this.editCreateObjArray
              );
              this.editObjArray.push(this.drawingTypePicker);
            } else {
              for (const tool in editingTools[editTool]) {
                if (editingTools[editTool][tool]) {
                  if (tool === "point") {
                    this.editCreateObjArray.push(
                      new InteractionPointEntity(editingTools[editTool][tool])
                    );
                  } else if (tool === "linestring") {
                    this.editCreateObjArray.push(
                      new InteractionLinestringEntity(
                        editingTools[editTool][tool]
                      )
                    );
                  } else if (tool === "polygon") {
                    this.editCreateObjArray.push(
                      new InteractionPolygonEntity(editingTools[editTool][tool])
                    );
                  }
                }
              }
              if (
                this.editCreateObjArray &&
                this.editCreateObjArray.length > 0
              ) {
                this.drawingTypePicker = new DrawingTypePicker(
                  this,
                  this.editCreateObjArray
                );
                this.editObjArray.push(this.drawingTypePicker);
              }
            }
          } else if (editingTools[editTool]) {
            if (editTool === "select") {
              this.editObjArray.push(
                new InteractionSelect(
                  this,
                  this.checkSelectOptions(props.editingTools.select),
                  this.checkPropertyOptions(props.editingTools.property)
                )
              );
            } else if (editTool === "remove") {
              this.editObjArray.push(
                new InteractionRemove(
                  this.checkSelectOptions(props.editingTools.remove)
                )
              );
            } else if (editTool === "move") {
              this.editObjArray.push(new InteractionMove(this));
            } else if (editTool === "scale") {
              this.editObjArray.push(new InteractionScale(this));
            } else if (editTool === "rotate") {
              this.editObjArray.push(new InteractionRotate(this));
            } else if (editTool === "upload") {
              this.editUploadObj = new Interaction3DPosition();

              this.popupInput3D = new PopupInput3D(this, this.editUploadObj);
              this.editObjArray.push(this.popupInput3D);
            }
          }
        }
      }

      if (this.editObjArray && this.editObjArray.length > 0) {
        // 편집 모음 클래스
        this.editToolPicker = new EditToolPicker(this, this.editObjArray);
      }
    }

    // 분석
    if (analysisToolPicker) {
      const analysisTools = props.analysisTools;
      if (analysisTools === "all") {
        this.analysisObjArray = [
          new InteractionVisibility(this),
          new InteractionClipping(this),
          new InteractionSplit(this),
        ];
      } else {
        this.analysisObjArray = [];
        for (const analysisTool in analysisTools) {
          if (analysisTools[analysisTool]) {
            if (analysisTool === "visibility") {
              this.analysisObjArray.push(new InteractionVisibility(this));
            } else if (analysisTool === "clipping") {
              this.analysisObjArray.push(new InteractionClipping(this));
            } else if (analysisTool === "split") {
              this.analysisObjArray.push(new InteractionSplit(this));
            }
          }
        }
      }

      if (this.analysisObjArray && this.analysisObjArray.length > 0) {
        this.analysisToolPicker = new AnalysisToolPicker(
          this,
          this.analysisObjArray
        );
      }
    }

    // 측정
    if (measurementToolPicker) {
      const measurementTools = props.measurementTools;

      if (measurementTools === "all") {
        this.measurementObjArray = [
          new InteractionAltitude(),
          new InteractionLengthStraight(),
          new InteractionLengthGround(),
          new InteractionLengthPlane(),
          new InteractionLengthVertical(),
          new InteractionLengthHorizontal(),
          new InteractionArea(),
          new InteractionVolume(this),
        ];
      } else {
        this.measurementObjArray = [];
        for (const measurementTool in measurementTools) {
          if (measurementTools[measurementTool]) {
            if (measurementTool === "altitude") {
              this.measurementObjArray.push(
                new InteractionAltitude(measurementTools[measurementTool])
              );
            } else if (measurementTool === "straight") {
              this.measurementObjArray.push(
                new InteractionLengthStraight(measurementTools[measurementTool])
              );
            } else if (measurementTool === "ground") {
              this.measurementObjArray.push(
                new InteractionLengthGround(measurementTools[measurementTool])
              );
            } else if (measurementTool === "plane") {
              this.measurementObjArray.push(
                new InteractionLengthPlane(measurementTools[measurementTool])
              );
            } else if (measurementTool === "vertical") {
              this.measurementObjArray.push(
                new InteractionLengthVertical(measurementTools[measurementTool])
              );
            } else if (measurementTool === "horizontal") {
              this.measurementObjArray.push(
                new InteractionLengthHorizontal(
                  measurementTools[measurementTool]
                )
              );
            } else if (measurementTool === "area") {
              this.measurementObjArray.push(
                new InteractionArea(measurementTools[measurementTool])
              );
            } else if (measurementTool === "volume") {
              this.measurementObjArray.push(
                new InteractionVolume(this, measurementTools[measurementTool])
              );
            }
          }
        }
      }

      if (this.measurementObjArray && this.measurementObjArray.length > 0) {
        this.measurementToolPicker = new MeasurementToolPicker(
          this,
          this.measurementObjArray
        );
      }
    }

    //-----------------------------보경---------------------------------

    // 도움말
    if (ditapNavigationHelpButton) {
      const navigationHelpContainer = this.toolbarTopBtn2;

      this.ditapNavigationHelpButton = new DitapNavigationHelpButton({
        viewer: this,
        container: navigationHelpContainer,
        instructionsInitiallyVisible: false,
      });

      if (!ditapHomeButton) {
        this.ditapNavigationHelpButton.helpPopup.classList.add("right-p-20");
      }
    }

    // 전체화면
    if (ditapFullscreenButton) {
      this.ditapFullscreenButton = new DitapFullscreenButton(
        this,
        props.fullscreenElement
      );
    }

    //-----------------------------보경---------------------------------

    this._ditapStorage = new DitapStorage();

    // Animation
    if (props.animation !== false || !defined(props.animation)) {
      this.animation.viewModel.dateFormatter = localeDateTimeFormatter;
      this.animation.viewModel.timeFormatter = (time) => {
        return localeDateTimeFormatter(time, "time");
      };

      if (props.timeline === false) {
        this.animation.mobileOpenTag.classList.add("bottom-0");
        this.animation.animationContainer?.classList.add("bottom-0");
        document
          .getElementsByClassName("cesium-viewer-bottom")[0]
          ?.classList.add("bottom-0");
        // console.log(this.animation.mobileOpenTag)
      }
    }

    if (
      props.animation === false &&
      (props.timeline === true || !defined(props.timeline))
    ) {
      document
        .getElementsByClassName("cesium-viewer-bottom")[0]
        ?.classList.add("left-0");
    }

    if (props.timeline === false && props.animation === false) {
      document
        .getElementsByClassName("cesium-viewer-bottom")[0]
        ?.classList.add("bottom-0", "left-0");
    }

    // TimeLine
    if (props.timeline !== false || !defined(props.timeline)) {
      this.timeline.makeLabel = (time) => {
        return localeDateTimeFormatter(time, "datetime");
      };
    }
    //---------------------이준----------------------------
    //타임라인 도형 생성
    const ditapTimeline = this.timeline;
    const timelineContainer = ditapTimeline?.container;
    const animationContainer = this.animation?.container;

    // 애니메이션 위젯과 타임라인이 동시에 있을 경우만 연결부 장식 요소 추가
    if (animationContainer && !timelineContainer) {
      animationContainer.querySelector(
        ".ditap-animation-bridge"
      ).style.display = "none";
    }

    this.resize();

    //-------------------------이준---------------------------
    this.setCameraControl();

    // 카메라 이동 감도 변경
    this.camera.percentageChanged = 0.00001;

    // 더블 클릭 시 시점 고정 제거
    this.screenSpaceEventHandler.removeInputAction(
      ScreenSpaceEventType.LEFT_DOUBLE_CLICK
    );
    //-------------------------이준---------------------------
  }

  /*
   * Ditap 객체 스토리지.<br>
   * Ditap Object Storage.
   * @type {DitapStorage}
   */
  set ditapStorage(ditapStorage) {
    if (ditapStorage instanceof DitapStorage) {
      this._ditapStorage = ditapStorage;
    }
  }
  get ditapStorage() {
    return this._ditapStorage;
  }

  /*
   * 도구 모음 컨테이너 요소.<br>
   * The Element of toolPickerContainer.
   * @type {Element}
   */
  set toolPickerContainer(toolPickerContainer) {
    this._toolPickerContainer = toolPickerContainer;
  }
  get toolPickerContainer() {
    return this._toolPickerContainer;
  }

  /*
   * 툴바 활성화 여부.<br>
   * Toolbar using state.
   *  @type {boolean}
   */
  set isToolbar(boolean) {
    this._isToolbar = boolean;
  }
  get isToolbar() {
    return this._isToolbar;
  }

  /*
   * 현재 실행중인 이벤트.<br>
   * Interaction currently running.
   * @type {object}
   */
  set currentEvent(currentEvent) {
    this._currentEvent = currentEvent;
  }
  get currentEvent() {
    return this._currentEvent;
  }

  /*
   * DitapPopup/DitapPanel을 관리하는 클래스.<br>
   * The class that manages the DitapPopup/DitapPanel.
   * @type {DitapPaneManager}
   */
  set ditapPaneManager(ditapPaneManager) {
    this._ditapPaneManager = ditapPaneManager;
  }
  get ditapPaneManager() {
    return this._ditapPaneManager;
  }

  /*
   * 모바일 전용 버튼 컨테이너 요소.<br>
   * Mobile-specific button container element.
   * @type {Element}
   */
  set mobileBtnWrapper(mobileBtnWrapper) {
    this._mobileBtnWrapper = mobileBtnWrapper;
  }
  get mobileBtnWrapper() {
    return this._mobileBtnWrapper;
  }

  /**
   * DitapFullscreenButton 조회.<br>
   * Gets the DitapFullscreenButton.
   * @type {DitapFullscreenButton}
   * @readonly
   */
  get fullscreenButton() {
    return this.ditapFullscreenButton;
  }

  get baseLayerPicker() {
    return this._ditapBaseLayerPicker;
  }
  set baseLayerPicker(baseLayerPicker) {
    this._baseLayerPicker = baseLayerPicker;
  }

  /**
   * DitapNavigationHelpButton 조회.<br>
   * Gets the DitapNavigationHelpButton.
   * @type {DitapNavigationHelpButton}
   * @readonly
   */
  get navigationHelpButton() {
    return this.ditapNavigationHelpButton;
  }

  /**
   * DitapHomeButton 조회.<br>
   * Gets the DitapHomeButton.
   * @type {DitapHomeButton}
   * @readonly
   */
  get homeButton() {
    return this.ditapHomeButton;
  }
  set homeButton(homeButton) {
    this.ditapHomeButton = homeButton;
  }

  /**
   * DitapGeocoder 조회.<br>
   * Gets the DitapGeocoder.
   * @type {DitapGeocoder}
   * @readonly
   */
  get geocoder() {
    return this.ditapGeocoder;
  }
  set geocoder(geocoder) {
    this.ditapGeocoder = geocoder;
  }

  /**
   * 위젯 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the widget. Should be called if permanently removing the widget from layout.
   */
  destroy() {
    if (this.homeButton) {
      this.homeButton.destroy();
      this.homeButton = undefined;
    }

    if (this.editToolPicker) {
      this.editToolPicker.destroy();
      this.editToolPicker = undefined;
    }

    if (this.analysisToolPicker) {
      this.analysisToolPicker.destroy();
      this.analysisToolPicker = undefined;
    }

    if (this.measurementToolPicker) {
      this.measurementToolPicker.destroy();
      this.measurementToolPicker = undefined;
    }

    if (this.geocoder) {
      this.geocoder.destroy();
      this.geocoder = undefined;
    }

    super.destroy();

    if (this._resizeEvent) {
      window.removeEventListener("resize", this._resizeEvent);
    }
    if (this._moreBtnEvent) {
      this._mobileMoreBtn.removeEventListener("click", this._moreBtnEvent);
    }

    this._resizeEvent = undefined;
    this._mobileMoreBtn = undefined;
  }

  /**
   * 베이스 레이어에 추가할 Imagery Provider 배열 조회.<br>
   * Gets the Array of Imagery Provider to be added to the base layer.
   * @return {ProviderViewModel[]} 조회된 providerViewModel 배열.<br> Array of providerViewModel
   *
   */
  createDefaultImageryProviderViewModels() {
    // * 배경지도 이미지 객체를 배열 형식으로 리턴해주는 함수
    const providerViewModels = [];

    // OSM 지도
    providerViewModels.push(
      new ProviderViewModel({
        name: "Open\u00adStreet\u00adMap",
        iconUrl: Utils.getUrlFromBase64(ConstantUrl.OSM_MAP_IMG_URL),
        tooltip: "",
        category: "Other",
        creationFunction: function () {
          return new OpenStreetMapImageryProvider({
            url: "https://a.tile.openstreetmap.org/",
          });
        },
      })
    );

    // DITAP 전국 정사영상
    providerViewModels.push(
      new ProviderViewModel({
        name: "Ditap Korea Map",
        iconUrl: Utils.getUrlFromBase64(ConstantUrl.DITAP_MAP_IMG_URL),
        tooltip: "Ditap Korea Map",
        category: "Other",
        creationFunction: function () {
          return new NationwideOrthoImageryProvider();
        },
      })
    );

    // Naver 지도
    providerViewModels.push(
      new ProviderViewModel({
        name: "Naver Map",
        iconUrl: Utils.getUrlFromBase64(ConstantUrl.NAVER_MAP_IMG_URL),
        tooltip: "",
        category: "Other",
        creationFunction: function () {
          return new NaverStaticImageryProvider({
            proxy: "/proxy/",
            clientId: "4n6heq5dbc",
            clientSecret: "5v5TjXXCYHuzyLfgN6RMisBrWqWrz2j0CFXsgRU5",
            mapType: "basic",
          });
        },
      })
    );

    // VWorld
    providerViewModels.push(
      new ProviderViewModel({
        name: "VWorld Korea Map",
        iconUrl: Utils.getUrlFromBase64(ConstantUrl.VWORLD_MAP_IMG_URL),
        tooltip: "VWorld Korea Map",
        category: "Other",
        creationFunction: function () {
          return new VWorldImageryProvider({
            apiKey: "ABB43517-2006-318D-A8DB-CAC4A4FBB5B5",
            mapType: "base",
          });
        },
      })
    );

    // 바로 E맵
    // 인증키 없음
    providerViewModels.push(
      new ProviderViewModel({
        name: "Ngii Map",
        iconUrl: Utils.getUrlFromBase64(ConstantUrl.NGII_MAP_IMG_URL),
        tooltip: "Ngii Map",
        category: "Other",
        creationFunction: function () {
          return new NgiiMapImageryProvider({
            apiKey: "657E858946F95A289AF493D992E1FF9A",
            mapType: "korean_map",
          });
        },
      })
    );

    // 지도(정사영상) 추가
    /*
      providerViewModels.push(
        new ProviderViewModel({
          name: "Ditap Korea Map",
          iconUrl: Utils.getUrlFromBase64(ConstantUrl.DITAP_MAP_IMG_URL),
          tooltip: "Ditap Korea Map",
          category: "Other",
          creationFunction: function () {
            return new UrlTemplateImageryProvider({
              // Geoserver 프록시 주소 입력
              url: `${location.origin}/Ditap/proxy/tileMap001.do?tilematrix={z}&tilerow={y}&tilecol={x}`,
              maximumLevel: 18,
            });
          },
        })
      );
    */

    return providerViewModels;
  }

  /**
   * 기본 레이어에 추가할 Terrain Provider의 배열 조회.<br>
   * Gets the Array of Terrain Provider to be added to the base layer.
   * @return {ProviderViewModel[]} 조회된 providerViewModel 배열.<br> Array of providerViewModel
   */
  createDefaultTerrainProviderViewModels() {
    const providerViewModels = [];

    providerViewModels.push(
      new ProviderViewModel({
        name: "WGS84 Ellipsoid",
        iconUrl: Utils.getUrlFromBase64(ConstantUrl.ELLIPSOIDE_TERRAIN_IMG_URL),
        tooltip: "WGS84 standard ellipsoid, also known as EPSG:4326",
        category: "Cesium ion",
        creationFunction: function () {
          return new EllipsoidTerrainProvider();
        },
      })
    );

    providerViewModels.push(
      new ProviderViewModel({
        name: "Ditap Korea Terrain",
        iconUrl: Utils.getUrlFromBase64(ConstantUrl.DITAP_TERRAIN_IMG_URL),
        tooltip: "",
        category: "Cesium ion",
        creationFunction: () => {
          return NationwideTerrainProvider.create();
        },
      })
    );

    // 지형(터레인) 추가
    /*
      providerViewModels.push(
        new ProviderViewModel({
          name: "Ditap Korea Terrain",
          iconUrl: Utils.getUrlFromBase64(
            ConstantUrl.DITAP_TERRAIN_IMG_URL
          ),
          tooltip: "",
          category: "Ditap",
          creationFunction: function () {
            return new CesiumTerrainProvider({
              // 터레인 주소 입력
              url: "터레인 주소",
            });
          },
        })
      );
    */

    return providerViewModels;
  }

  /*
   * Ditap 헤더 요소 생성.<br>
   * Create Element if ditap header.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createMobileToolbar(viewer) {
    const mobileBtnWrapper = document.createElement("div");
    mobileBtnWrapper.classList.add("ditap-mobile-button-wrap");

    const moreBtn = document.createElement("button");
    moreBtn.classList.add("ditap-mobile-btn", "ditap-more-btn");

    const p1 = document.createElement("p");
    p1.classList.add("ditap-function-btn-img");
    moreBtn.appendChild(p1);
    mobileBtnWrapper.appendChild(moreBtn);

    const btnList = document.createElement("ul");

    const li1 = document.createElement("li");
    li1.classList.add("position-guide", "js-position-home");
    btnList.appendChild(li1);

    const li2 = document.createElement("li");
    li2.classList.add("position-guide", "js-position-basemap");
    btnList.appendChild(li2);

    const li3 = document.createElement("li");
    li3.classList.add("position-guide", "js-position-search");
    btnList.appendChild(li3);

    const li4 = document.createElement("li");
    li4.classList.add("position-guide", "js-position-edit");
    btnList.appendChild(li4);

    const li5 = document.createElement("li");
    li5.classList.add("position-guide", "js-position-analysis");
    btnList.appendChild(li5);

    const li6 = document.createElement("li");
    li6.classList.add("position-guide", "js-position-measure");
    btnList.appendChild(li6);

    const li7 = document.createElement("li");
    li7.classList.add("position-guide", "js-position-help");
    btnList.appendChild(li7);

    const li8 = document.createElement("li");
    li8.classList.add("position-guide", "js-position-fullscreen");
    btnList.appendChild(li8);

    btnList.classList.add("ditap-func-btn-list", "hide");
    mobileBtnWrapper.appendChild(btnList);

    moreBtn.addEventListener(
      "click",
      (this._moreBtnEvent = () => {
        if (btnList.classList.contains("hide")) {
          btnList.classList.remove("hide");
        } else {
          btnList.classList.add("hide");
        }
      })
    );

    this.mobileBtnWrapper = mobileBtnWrapper;
    this._mobileMoreBtn = moreBtn;

    const resizeOriginal = () => {
      // 윈도우의 현재 너비를 확인
      const currentWidth = window.innerWidth;

      if (currentWidth > 600) {
        btnList.classList.add("hide");
      } else if (this.getOpenToolBar()) {
        btnList.classList.remove("hide");
        // btnList.style.display = "block";
      }
    };
    this._resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };
    window.addEventListener("resize", this._resizeEvent);
    viewer.container.querySelector(".cesium-viewer").append(mobileBtnWrapper);
  }

  /*
   * Ditap 툴바 요소 생성.<br>
   * Create Element if ditap tool bar.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createToolBar(viewer) {
    // wrapper
    const toolbarTopDiv = document.createElement("div");
    toolbarTopDiv.classList.add(
      "ditap-toolbar-top-wrap",
      "js-ditap-toolbal-top-wrap"
    );

    if (viewer.ditapBaseLayerPicker && viewer.ditapNavigationHelpButton) {
      toolbarTopDiv.innerHTML = `<div class="ditap-toolbar-top-btn-wrap js-ditap-toolbal-top-btn-wrap">
      <div class="ditap-toolbar-top-btn js-ditap-toolbal-top-btn1"></div>
      <div class="ditap-toolbar-top-btn js-ditap-toolbal-top-btn2"></div>
    </div>`;
    } else if (viewer.ditapBaseLayerPicker) {
      toolbarTopDiv.innerHTML = `<div class="ditap-toolbar-top-btn-wrap js-ditap-toolbal-top-btn-wrap">
    <div class="ditap-toolbar-top-btn js-ditap-toolbal-top-btn1"></div>
  </div>`;
    } else if (viewer.ditapNavigationHelpButton) {
      toolbarTopDiv.innerHTML = `<div class="ditap-toolbar-top-btn-wrap js-ditap-toolbal-top-btn-wrap">
      <div class="ditap-toolbar-top-btn js-ditap-toolbal-top-btn2"></div>
    </div>`;
    }

    const toolbarRightDiv = document.createElement("div");
    toolbarRightDiv.classList.add(
      "ditap-toolbar-right-btn-wrap",
      "js-ditap-toolbal-right-btn-wrap"
    );

    if (this.isToolPicker) {
      toolbarRightDiv.innerHTML = `
      <div>
        <ul class="ditap-toolbar-right-list js-ditap-toolbar-right-list"></ul>
      </div>
      `;
    }
    viewer.container.querySelector(".cesium-viewer").append(toolbarTopDiv);
    viewer.container.querySelector(".cesium-viewer").append(toolbarRightDiv);
  }

  /*
   * 뷰어에서 선언된 모든 인터렉션 조회.<br>
   * Gets the currently running Interactions.
   * @returns {any[]} 이벤트 배열.<br> array of Intaractions
   */
  getOnInteractions() {
    const onInteraction = [];
    const allObjArray = [
      ...(this.editCreateObjArray || []),
      ...(this.editObjArray || []),
      ...(this.analysisObjArray || []),
      ...(this.measurementObjArray || []),
      this.editUploadObj || [],
    ];

    allObjArray.forEach((obj) => {
      if (
        (obj instanceof InteractionPointEntity ||
          obj instanceof InteractionLinestringEntity ||
          obj instanceof InteractionPolygonEntity ||
          obj instanceof InteractionSelect ||
          obj instanceof InteractionRemove ||
          obj instanceof InteractionSplit ||
          obj instanceof InteractionAltitude ||
          obj instanceof InteractionLengthStraight ||
          obj instanceof InteractionLengthGround ||
          obj instanceof InteractionLengthPlane ||
          obj instanceof InteractionLengthVertical ||
          obj instanceof InteractionLengthHorizontal ||
          obj instanceof InteractionArea ||
          obj instanceof InteractionVolume ||
          obj instanceof InteractionMove ||
          obj instanceof InteractionScale ||
          obj instanceof InteractionRotate ||
          obj instanceof InteractionVisibility ||
          obj instanceof InteractionClipping ||
          obj instanceof Interaction3DPosition) &&
        obj.isOn
      ) {
        onInteraction.push(obj);
      }
    });

    return onInteraction;
  }

  /**
   * 뷰어에서 선언된 모든 인터렉션 종료.<br>
   * Destroy the running Interaction.
   * @param {Function} callback 이벤트 제거 후 실행 할 콜백함수.<br> Callback function to be executed after removing the event.
   */
  offAllInterations(callback) {
    if (this.getOnInteractions()) {
      const onInteractions = this.getOnInteractions();

      if (onInteractions.length > 0) {
        onInteractions.forEach((onInteraction) => {
          if (onInteraction instanceof InteractionSplit) {
            if (callback) {
              callback();
            }
            return;
          }
          // 인스턴스 삭제
          if (onInteraction.abort) {
            onInteraction.abort();
          }

          // 핸들러 삭제
          if (onInteraction.off) {
            if (
              (onInteraction instanceof InteractionMove ||
                onInteraction instanceof InteractionScale ||
                onInteraction instanceof InteractionRotate) &&
              onInteraction.selectObject
            ) {
              onInteraction.off(true, callback);
            } else {
              onInteraction.off(false);

              if (callback) {
                callback();
              }
            }
          }
        });
      } else if (callback) {
        callback();
      }
    }
  }

  /**
   * 뷰어에 생성된 모든 도형 엔티티 파괴.<br>
   * Destroying all Geometric entities created on the viewer.
   */
  removeAllGeometric() {
    const measurements = [
      this.ditapStorage.pointEntity,
      this.ditapStorage.linestringEntity,
      this.ditapStorage.polygonEntity,
    ];
    this.removeStorageEntity(measurements);

    this.offAllInterations();
  }

  /**
   * 뷰어에 생성된 모든 측정 엔티티 파괴.<br>
   * Destroying all measurement entities created on the viewer.
   */
  removeAllMeasurement() {
    const measurements = [
      this.ditapStorage.measurementAltitude,
      this.ditapStorage.measurementArea,
      this.ditapStorage.measurementLengthGround,
      this.ditapStorage.measurementLengthPlane,
      this.ditapStorage.measurementLengthStraight,
      this.ditapStorage.measurementLengthVertical,
      this.ditapStorage.measurementLengthHorizontal,
      this.ditapStorage.measurementVolume,
      this.ditapStorage.volumePopups,
    ];
    this.removeStorageEntity(measurements);

    this.offAllInterations();
  }

  /**
   * 뷰어에 생성된 모든 분석 엔티티 파괴.<br>
   * Destroying all analysis entities created on the viewer.
   */
  removeAllAnalysis() {
    const analysisEntityList = [
      this.ditapStorage.analysisVisibility,
      this.ditapStorage.visibilityPopups,
      this.ditapStorage.analysisClipping,
    ];
    this.removeStorageEntity(analysisEntityList);

    this.offAllInterations();
  }

  /**
   * 해당 객체 스토리지 제거.<br>
   * Remove the storage of the corresponding object.
   * @param {object[]} storage 제거 할 스토리지. <br> Storage to be removed.
   */
  removeStorageEntity(storage) {
    storage.forEach((measurementType) => {
      Object.values(measurementType).forEach((measurement) =>
        measurement.destroy()
      );
      measurementType = {};
    });
  }

  /*
   * 지도에서 툴바 버튼이 클릭 되었을 때 실행.<br>
   * Function to be executed when the map toolbar button is clicked
   * @param {AnalysisToolPicker|DitapBaseLayerPicker|DitapNavigationHelpButton|EditToolPicker|MeasurementToolPicker} toolbarObj 클릭된 툴바 객체.<br> object of toolbar
   */
  clickToolbar(toolbarObj) {
    if (this.isToolbar) {
      this.closeToolbarAll(toolbarObj);
      // this.offAllInterations();
    }

    if (toolbarObj instanceof DitapNavigationHelpButton) {
      if (toolbarObj.helpButton.classList.contains("js-toolbar-on")) {
        this.isToolbar = false;
        toolbarObj.close();
      } else {
        this.isToolbar = true;
        toolbarObj.open();

        // 편집 기능 - 생성 팝업 닫기
        if (this.container.getElementsByClassName("ditap-drawing-popup")[0]) {
          this.drawingTypePicker.close();
        }

        if (this.container.getElementsByClassName("ditap-data-add-popup")[0]) {
          this.popupInput3D.close();
        }

        if (
          this.ditapGeocoder?._searchContainer.classList.contains(
            "ditap-mobile-search"
          )
        ) {
          this.ditapGeocoder._searchContainer.style.display = "none";
          this.ditapGeocoder.isOpen = false;
        }
      }
    } else if (toolbarObj instanceof DitapBaseLayerPicker) {
      if (!toolbarObj.mapPopup.classList.contains("hide")) {
        this.isToolbar = false;
        toolbarObj.close();
      } else {
        this.isToolbar = true;
        toolbarObj.open();

        // 편집 기능 - 생성 팝업 닫기
        if (this.container.getElementsByClassName("ditap-drawing-popup")[0]) {
          this.drawingTypePicker.close();
        }
        if (this.container.getElementsByClassName("ditap-data-add-popup")[0]) {
          this.popupInput3D.close();
        }
        if (
          this.ditapGeocoder?._searchContainer.classList.contains(
            "ditap-mobile-search"
          )
        ) {
          this.ditapGeocoder._searchContainer.style.display = "none";
          this.ditapGeocoder.isOpen = false;
        }
      }
    } else if (
      toolbarObj instanceof MeasurementToolPicker ||
      toolbarObj instanceof EditToolPicker ||
      toolbarObj instanceof AnalysisToolPicker
    ) {
      if (toolbarObj.toolPicker.classList.contains("js-toolbar-on")) {
        this.isToolbar = false;
        toolbarObj.close(this);
      } else {
        this.isToolbar = true;
        toolbarObj.open(this);

        // 편집 기능 - 생성 팝업 닫기
        if (this.container.getElementsByClassName("ditap-drawing-popup")[0]) {
          this.drawingTypePicker.close();
        }
        if (this.container.getElementsByClassName("ditap-data-add-popup")[0]) {
          this.popupInput3D.close();
        }
        if (
          this.ditapGeocoder?._searchContainer.classList.contains(
            "ditap-mobile-search"
          )
        ) {
          this.ditapGeocoder._searchContainer.style.display = "none";
          this.ditapGeocoder.isOpen = false;
        }
      }
    } else if (toolbarObj instanceof DitapGeocoder) {
      toolbarObj.toggle();
    }
  }

  /*
   * 모바일 전용 툴바 상태 조회.
   * Query the status of the mobile-specific toolbar.
   * @returns {boolean} 상태. status
   */
  getOpenToolBar() {
    const btns = this.container.getElementsByClassName(
      "ditap-function-btn-img"
    );
    let result = false;
    for (let i = 0; i < btns.length; i++) {
      if (btns[i].classList.contains("on")) {
        result = true;
        break;
      }
    }
    return result;
  }

  /*
   * 클릭된 툴바 외 모든 툴바 닫기.<br>
   * Close all toolbars except the clicked toolbar. (Edit, Analysis, Measurement)
   * @param {EditToolPicker|MeasurementToolPicker|AnalysisToolPicker|DitapNavigationHelpButton|DitapBaseLayerPicker} 클릭된 툴바.<br> Clicked toolbar
   */
  closeToolbarAll(toolbarObj) {
    const toolbarArray = [
      this.editToolPicker,
      this.measurementToolPicker,
      this.analysisToolPicker,
      this.ditapNavigationHelpButton,
      this.ditapBaseLayerPicker,
    ];

    for (const toolbar of toolbarArray) {
      if (toolbar && toolbar !== toolbarObj) {
        toolbar.close();
      }
    }
  }

  /*
   * InteractionMove 인스턴스 조회.<br>
   * get instance of InteractionMove
   * @returns {InteractionMove} 조회된 인스턴스.<br> Retrieved instance.
   */
  getInteractionMove() {
    let interaction;
    this.editObjArray.forEach((editObj) => {
      if (editObj instanceof InteractionMove) {
        interaction = editObj;
      }
    });
    return interaction;
  }

  /*
   * InteractionRotate 인스턴스 조회.<br>
   * get instance of InteractionRotate
   * @returns {InteractionRotate} 조회된 인스턴스.<br> Retrieved instance.
   */
  getInteractionRotate() {
    let interaction;
    this.editObjArray.forEach((editObj) => {
      if (editObj instanceof InteractionRotate) {
        interaction = editObj;
      }
    });
    return interaction;
  }

  /*
   * InteractionScale 인스턴스 조회.<br>
   * get instance of InteractionScale
   * @returns {InteractionScale} 조회된 인스턴스.<br> Retrieved instance.
   */
  getInteractionScale() {
    let interaction;
    this.editObjArray.forEach((editObj) => {
      if (editObj instanceof InteractionScale) {
        interaction = editObj;
      }
    });
    return interaction;
  }

  /*
   * InteracionSplit 인스턴스 조회.<br>
   * get instance of InteracionSplit
   * @returns {InteractionSplit} 조회된 인스턴스.<br> Retrieved instance.
   */
  getInteractionSplit() {
    let interaction;
    this.analysisObjArray.forEach((analysisObj) => {
      if (analysisObj instanceof InteractionSplit) {
        interaction = analysisObj;
      }
    });
    return interaction;
  }

  /**
   * check the object of select.
   * @param {object} options object of select options.
   * @returns {object} Verified select options.
   */
  checkSelectOptions(options) {
    const newOptions = {};
    if (typeof options === "object") {
      if (!options.hoverColor || !(options.hoverColor instanceof Color)) {
        newOptions.hoverColor = Color.fromCssColorString("#BDBDBD");
      } else {
        newOptions.hoverColor = options.hoverColor;
      }
      if (!options.selectedColor || !(options.selectedColor instanceof Color)) {
        newOptions.selectedColor = Color.fromCssColorString("#9DCFFF");
      } else {
        newOptions.selectedColor = options.selectedColor;
      }
    } else {
      newOptions.hoverColor = Color.fromCssColorString("#BDBDBD");
      newOptions.selectedColor = Color.fromCssColorString("#9DCFFF");
    }
    return newOptions;
  }

  /**
   * check the property type.
   * @param {object} property object of property.
   * @returns {object} Verified property type.
   */
  checkPropertyOptions(property) {
    const newProperty = {};
    // 1. property 객체가 없으면 default(popup)
    if (!property) {
      return (newProperty["type"] = "popup");
    }

    // 2. property 객체가 있으면 해당 타입으로
    if (property["type"]?.toLowerCase().replace(/(\s*)/g, "") === "panel") {
      newProperty["type"] = "panel";
    } else {
      newProperty["type"] = "popup";
    }

    // 3. property 내부에 element 확인
    if (newProperty.type === "popup") {
      return newProperty;
    } else if (
      Object.prototype.toString.call(property["container"]) ===
      "[object HTMLDivElement]"
    ) {
      // 엘리먼트일 경우
      newProperty["container"] = property["element"];
    } else if (
      typeof property["container"] === "string" &&
      document.getElementById(property["container"])
    ) {
      // string일 경우
      newProperty["container"] = document.getElementById(property["container"]);
    } else {
      // default
      newProperty["container"] = document.getElementById("ditapPanel");
    }

    if (
      property.propertyCallback instanceof Function ||
      property.propertyCallback === undefined
    ) {
      newProperty["propertyCallback"] = property.propertyCallback;
    } else {
      console.error("InteractionSelect propertyCallback type error");
    }

    return newProperty;
  }

  //타임라인 사이즈 변경
  // resize() {
  //   const cesiumWidget = this._cesiumWidget;
  //   const container = this._container;
  //   const width = container.clientWidth;
  //   const height = container.clientHeight;
  //   const animationExists = defined(this._animation);
  //   const timelineExists = defined(this._timeline);

  //   cesiumWidget.resize();

  //   if (width === this._lastWidth && height === this._lastHeight) {
  //     return;
  //   }

  //   const panelMaxHeight = height - 125;
  //   const baseLayerPickerDropDown = this._baseLayerPickerDropDown;

  //   if (defined(baseLayerPickerDropDown)) {
  //     baseLayerPickerDropDown.style.maxHeight = `${panelMaxHeight}px`;
  //   }

  //   if (defined(this._geocoder)) {
  //     const geocoderSuggestions = this._geocoder.searchSuggestionsContainer;
  //     geocoderSuggestions.style.maxHeight = `${panelMaxHeight}px`;
  //   }

  //   if (defined(this._infoBox)) {
  //     this._infoBox.viewModel.maxHeight = panelMaxHeight;
  //   }

  //   const timeline = this._timeline;
  //   let animationContainer;
  //   let animationWidth = 0;
  //   let creditLeft = 0;
  //   let creditBottom = 0;

  //   if (
  //     animationExists &&
  //     window.getComputedStyle(this._animation.container).visibility !== "hidden"
  //   ) {
  //     const lastWidth = this._lastWidth;
  //     animationContainer = this._animation.container;
  //     if (width > 900) {
  //       animationWidth = 190;
  //       if (lastWidth <= 900) {
  //         animationContainer.style.width = "190px";
  //         animationContainer.style.height = "145px";
  //         this._animation.resize();
  //       }
  //     } else if (width >= 600) {
  //       animationWidth = 190;
  //       if (lastWidth < 600 || lastWidth > 900) {
  //         animationContainer.style.width = "190px";
  //         animationContainer.style.height = "145px";
  //         this._animation.resize();
  //       }
  //     } else {
  //       animationWidth = 190;
  //       if (lastWidth > 600 || lastWidth === 0) {
  //         animationContainer.style.width = "190px";
  //         animationContainer.style.height = "145px";
  //         this._animation.resize();
  //       }
  //     }
  //     creditLeft = animationWidth + 5;
  //   }

  //   if (
  //     timelineExists &&
  //     window.getComputedStyle(this._timeline.container).visibility !== "hidden"
  //   ) {
  //     const fullscreenButton = this._fullscreenButton;
  //     const vrButton = this._vrButton;
  //     const timelineContainer = timeline.container;
  //     const timelineStyle = timelineContainer.style;

  //     creditBottom = timelineContainer.clientHeight + 3;
  //     timelineStyle.left = `${animationWidth}px`;

  //     let pixels = 0;
  //     if (defined(fullscreenButton)) {
  //       pixels += fullscreenButton.container.clientWidth;
  //     }
  //     if (defined(vrButton)) {
  //       pixels += vrButton.container.clientWidth;
  //     }

  //     timelineStyle.right = `${pixels}px`;
  //     timeline.resize();
  //   }

  //   this._bottomContainer.style.left = `${creditLeft}px`;
  //   this._bottomContainer.style.bottom = `${creditBottom}px`;

  //   this._lastWidth = width;
  //   this._lastHeight = height;
  // }

  /*
   * 마우스 이벤트 기반으로 카메라 설정 변경. <br>
   * Change camera settings based on mouse events.
   */
  setCameraControl() {
    const cameraController = this.scene?.screenSpaceCameraController;
    if (cameraController) {
      cameraController.zoomEventTypes = [
        CameraEventType.WHEEL,
        CameraEventType.PINCH,
      ];
      cameraController.tiltEventTypes[0] = CameraEventType.RIGHT_DRAG; // 우클릭 카메라 회전
    } else {
      console.log("no camera controller");
    }
  }
}
export default DitapViewer;
